import { AppBar, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, Grid, IconButton, Menu, Paper, Stack, Toolbar, Typography } from '@vapor/material'
import { MenuItemProps, MenuItem, useMediaQuery, useTheme } from '@mui/material'
import { Fragment, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Action, BasicAction, Filter, FiltersAppearance, MenuOnlyAction, PrimaryAction, RichGraphQLDataGridProps } from './rich-graphql-data-grid.types'
import FormItem from '../form-fields/form-item/form-item'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical, faFilter } from '@fortawesome/free-solid-svg-icons'
import GraphQLDataGrid from '../graphql-data-grid/graphql-data-grid'
import { GraphQLDataGridRefType } from 'src/graphql-data-grid/graphql-data-grid.types'

function RichGraphQLDataGrid<QueryData extends object, QueryRecordType extends object, QueryVariables extends object, Filter>(
  props: RichGraphQLDataGridProps<QueryData, QueryRecordType, QueryVariables, Filter>,
) {
  const {
    actions,
    filters,
    variables,
    otherFiltersAppearance = 'DRAWER',
    i18n = {
      'filters.filters': 'Filters',
      'filters.others': 'Other Filters',
      'filters.close': 'Close',
      'filters.apply': 'Apply',
    },
    ...graphqlDatagridProps
  } = props

  /* ---------------------------------------------
  --------------------- FILTERS ------------------
  ------------------------------------------------ */
  const [currentFilterValue, setCurrentFilterValue] = useState<Filter>()

  const primaryFilters = (filters || []).filter((f) => !f.hidden).filter((f) => f.primary)
  const othersFilters = (filters || []).filter((f) => !f.hidden).filter((f) => !f.primary)

  const primaryFilterSerializations = useMemo(() => {
    return getFiltersSerializations(currentFilterValue, primaryFilters)
  }, [currentFilterValue, primaryFilters])

  const othersFilterSerializations = useMemo(() => {
    return getFiltersSerializations(currentFilterValue, othersFilters)
  }, [currentFilterValue, othersFilters])

  const ref = useRef<GraphQLDataGridRefType>(null)

  useEffect(() => {
    ref.current?.resetPagination()
  }, [currentFilterValue])

  const onFilterValueChange = useCallback(
    (newFilter: Filter) => {
      setCurrentFilterValue(newFilter)
    },
    [setCurrentFilterValue],
  )

  const graphQLDataGridVariables = useCallback(
    ({ pagination }) => {
      return variables({ pagination, filter: currentFilterValue })
    },
    [variables, currentFilterValue],
  )

  // FILTERS DETAILS
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const [tmpFilterValue, setTmpFilterValue] = useState<Filter>()

  const onTmpFilterValueChange = useCallback(
    (newFilter: Filter) => {
      setTmpFilterValue(newFilter)
    },
    [setTmpFilterValue],
  )

  const showFiltersDetails = useCallback(() => {
    setTmpFilterValue(currentFilterValue)
    setFiltersOpen(true)
  }, [setFiltersOpen, setTmpFilterValue, currentFilterValue])

  const applyFiltersDetails = useCallback(() => {
    setCurrentFilterValue(tmpFilterValue)
    setFiltersOpen(false)
  }, [setFiltersOpen, setCurrentFilterValue, tmpFilterValue])

  const showFiltersDetailsButton = (
    <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faFilter} />} onClick={() => showFiltersDetails()} size="small">
      {othersFilterSerializations.length > 0 ? `${i18n['filters.others']} (${othersFilterSerializations.length})` : i18n['filters.others']}
    </Button>
  )

  const filterDetailsTitle = (
    <>
      <Typography sx={{ display: { xs: 'flex', md: 'none' } }}>{i18n['filters.filters']}</Typography>
      <Typography sx={{ display: { xs: 'none', md: 'flex' } }}>{i18n['filters.others']}</Typography>
    </>
  )

  const filterDetailsBody = (
    <>
      <Stack spacing={2} direction="column" sx={{ display: { xs: 'flex', md: 'none' } }}>
        {primaryFilters.map((f, index) => (
          <FormItem key={index} fieldLabel={f.label} forceLabelMargin fieldTooltip={f.tooltip}>
            {f.component({ value: tmpFilterValue, onChange: onTmpFilterValueChange })}
          </FormItem>
        ))}
      </Stack>
      <Stack spacing={2} direction="column">
        {othersFilters.map((f, index) => (
          <FormItem key={index} fieldLabel={f.label} forceLabelMargin fieldTooltip={f.tooltip}>
            {f.component({ value: tmpFilterValue, onChange: onTmpFilterValueChange })}
          </FormItem>
        ))}
      </Stack>
    </>
  )
  const filterDetailsControls = (
    <Stack spacing={2} direction={'row-reverse'}>
      <Button variant="contained" onClick={() => applyFiltersDetails()}>
        {i18n['filters.apply']}
      </Button>
      <Button variant="outlined" onClick={() => setFiltersOpen(false)}>
        {i18n['filters.close']}
      </Button>
    </Stack>
  )
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.up('sm'))

  const filtersDetails: { [key in FiltersAppearance]: ReactElement } = {
    PAGE: (
      <Grid sx={{ display: filtersOpen ? 'block' : 'none' }}>
        <h1>{filterDetailsTitle}</h1>
        {filterDetailsBody}
        <AppBar data-testid="toolbar" position="absolute" color="default" sx={{ top: 'auto', bottom: 0, width: '100%' }}>
          <Toolbar sx={{ justifyContent: 'right' }}>{filterDetailsControls}</Toolbar>
        </AppBar>
      </Grid>
    ),
    DRAWER: (
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: `${sm ? '40%' : '100%'} !important`,
            minWidth: sm ? '640px' : '100%',
          },
        }}
      >
        <Paper elevation={0} sx={{ p: '16px', m: '16px' }}>
          <h1>{filterDetailsTitle}</h1>
          {filterDetailsBody}
          <AppBar data-testid="toolbar" position="absolute" color="default" sx={{ top: 'auto', bottom: 0, width: '100%' }}>
            <Toolbar sx={{ justifyContent: 'right' }}>{filterDetailsControls}</Toolbar>
          </AppBar>
        </Paper>
      </Drawer>
    ),
    DIALOG: (
      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} fullWidth scroll="body">
        <DialogTitle>{filterDetailsTitle}</DialogTitle>
        <DialogContent>{filterDetailsBody}</DialogContent>
        <DialogActions>
          <Toolbar sx={{ justifyContent: 'right' }}>{filterDetailsControls}</Toolbar>
        </DialogActions>
      </Dialog>
    ),
  }

  /* ---------------------------------------------
  --------------------- ACTIONS ------------------
  ------------------------------------------------ */

  const primaryAction = (actions || []).find((a) => (a as PrimaryAction).primary) as PrimaryAction
  const leftActions = (actions || []).filter((a) => a.position === undefined || a.position === 'left') as (PrimaryAction | BasicAction)[]
  const rightActions = (actions || []).filter((a) => a.position === 'right') as (PrimaryAction | BasicAction)[]
  const menuOnlyActions = (actions || []).filter((a) => a.position === 'menu-only') as MenuOnlyAction[]
  const mobileMenuActions = (actions || []).filter((a) => a.menuItem && !(a as PrimaryAction).primary) as Action[]

  const [menuActionsAnchorEl, setMenuActionsAnchorEl] = useState<(EventTarget & HTMLElement) | null>(null)
  const onMenuActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuActionsAnchorEl(event.currentTarget)
  }
  const onMenuActionsClose = () => {
    setMenuActionsAnchorEl(null)
  }
  const onMenuItemClick = (menuItem: ReactElement<MenuItemProps>, event: React.MouseEvent<HTMLLIElement>) => {
    setMenuActionsAnchorEl(null)
    if (menuItem.props.onClick) menuItem.props.onClick(event)
  }

  const [mobileMenuActionsAnchorEl, setMobileMenuActionsAnchorEl] = useState<(EventTarget & HTMLElement) | null>(null)
  const onMobileMenuActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuActionsAnchorEl(event.currentTarget)
  }
  const onMobileMenuActionsClose = () => {
    setMobileMenuActionsAnchorEl(null)
  }
  const onMobileMenuItemClick = (menuItem: ReactElement<MenuItemProps>, event: React.MouseEvent<HTMLLIElement>) => {
    setMenuActionsAnchorEl(null)
    if (menuItem.props.onClick) menuItem.props.onClick(event)
  }

  const mobileFilters = () => {
    if ((filters ?? []).length > 0) {
      return (
        <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faFilter} />} onClick={() => showFiltersDetails()} size="small">
          {primaryFilterSerializations.length + othersFilterSerializations.length > 0
            ? `${i18n['filters.filters']} (${primaryFilterSerializations.length + othersFilterSerializations.length})`
            : i18n['filters.filters']}
        </Button>
      )
    } else {
      return <div></div>
    }
  }

  const mobileActions = (
    <Stack direction="row" sx={{ justifyContent: 'right' }}>
      {primaryAction && (
        <>
          <Grid sx={{ display: { xs: 'block', sm: 'none' } }}>{primaryAction.mobileComponent}</Grid>
          <Grid sx={{ display: { xs: 'none', sm: 'block' } }}>{primaryAction.component}</Grid>
        </>
      )}
      {mobileMenuActions.length > 0 && (
        <>
          <IconButton onClick={onMobileMenuActionsClick} size="small">
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </IconButton>
          <Menu open={mobileMenuActionsAnchorEl !== null} anchorEl={mobileMenuActionsAnchorEl} onClose={onMobileMenuActionsClose}>
            {mobileMenuActions.map((a, index) => (
              <MenuItem {...a.menuItem!.props} key={index} onClick={(e) => onMobileMenuItemClick(a.menuItem!, e)} />
            ))}
          </Menu>
        </>
      )}
    </Stack>
  )

  let filtersAndActions
  if ((primaryFilters.length || rightActions.length) && primaryFilters.length <= 4 && leftActions.length === 0) {
    // SIMPLE VERSION
    const desktopFilters = (
      <Stack spacing={2} direction="row">
        {primaryFilters.map((f, index) => (
          <FormItem key={index} fieldLabel={f.label} forceLabelMargin fieldTooltip={f.tooltip}>
            {f.component({ value: currentFilterValue, onChange: onFilterValueChange })}
          </FormItem>
        ))}
        {othersFilters.length > 0 && <FormItem forceLabelMargin>{showFiltersDetailsButton}</FormItem>}
      </Stack>
    )
    const desktopActions = (
      <Grid item xs="auto">
        <Stack spacing={2} direction="row" sx={{ justifyContent: 'right' }}>
          {rightActions.map((a, index) => (
            <Fragment key={index}>{a.component}</Fragment>
          ))}
        </Stack>
      </Grid>
    )
    filtersAndActions = (
      <Grid container item direction="row" sx={{ margin: '15px 0px' }} xs="auto">
        <Grid item xs>
          <Grid container sx={{ display: { xs: 'none', md: 'block' } }}>
            {desktopFilters}
          </Grid>
          <Grid container sx={{ display: { xs: 'block', md: 'none' } }}>
            {mobileFilters()}
          </Grid>
        </Grid>
        <Grid item xs="auto" sx={{ alignSelf: 'end' }}>
          <Grid container sx={{ display: { xs: 'none', md: 'block' } }}>
            {desktopActions}
          </Grid>
          <Grid container sx={{ display: { xs: 'block', md: 'none' } }}>
            {mobileActions}
          </Grid>
        </Grid>
        {filtersDetails[otherFiltersAppearance]}
      </Grid>
    )
  } else {
    // COMPLETE VERSION
    const desktopFilters = filters && filters.length && (
      <>
        <Stack spacing={2} direction="row" sx={{ margin: '15px 0px' }}>
          {primaryFilters.map((f, index) => (
            <FormItem key={index} fieldLabel={f.label} forceLabelMargin fieldTooltip={f.tooltip}>
              {f.component({ value: currentFilterValue, onChange: onFilterValueChange })}
            </FormItem>
          ))}
          {othersFilters.length > 0 && <FormItem forceLabelMargin>{showFiltersDetailsButton}</FormItem>}
        </Stack>
        <Stack spacing={2} direction="row" sx={{ margin: '15px 0px' }}>
          {othersFilterSerializations.map((f) => (
            <Chip
              label={f.serialization}
              onDelete={() => {
                if (currentFilterValue) {
                  setCurrentFilterValue(f.filter.reset(currentFilterValue))
                }
              }}
            />
          ))}
        </Stack>
      </>
    )

    const desktopActions = actions && actions.length && (
      <Grid container direction="row" sx={{ margin: '15px 0px' }}>
        <Grid item xs>
          <Stack spacing={2} direction="row">
            {leftActions.map((a, index) => (
              <Fragment key={index}>{a.component}</Fragment>
            ))}
            {menuOnlyActions.length > 0 && (
              <>
                <IconButton onClick={onMenuActionsClick} size="small">
                  <FontAwesomeIcon icon={faFilter} />
                </IconButton>
                <Menu open={menuActionsAnchorEl !== null} anchorEl={menuActionsAnchorEl} onClose={onMenuActionsClose}>
                  {menuOnlyActions.map((a, index) => (
                    <MenuItem {...a.menuItem.props} key={index} onClick={(e) => onMenuItemClick(a.menuItem, e)} />
                  ))}
                </Menu>
              </>
            )}
          </Stack>
        </Grid>
        <Grid item xs="auto">
          <Stack spacing={2} direction="row" sx={{ justifyContent: 'right' }}>
            {rightActions.map((a, index) => (
              <Fragment key={index}>{a.component}</Fragment>
            ))}
          </Stack>
        </Grid>
      </Grid>
    )
    filtersAndActions = (
      <Grid container item xs={12}>
        <Grid item container sx={{ display: { xs: 'none', md: 'block' } }}>
          {desktopFilters}
          {desktopFilters && desktopActions && <Divider sx={{ width: '100%' }} />}
          {desktopActions}
        </Grid>
        <Grid item container sx={{ display: { xs: 'block', md: 'none' } }}>
          <Grid container direction="row" sx={{ margin: '15px 0px' }}>
            <Grid item xs>
              {mobileFilters()}
            </Grid>
            <Grid item xs="auto" sx={{ alignSelf: 'end' }}>
              {mobileActions}
            </Grid>
          </Grid>
        </Grid>
        {filtersDetails[otherFiltersAppearance]}
      </Grid>
    )
  }

  return (
    <>
      {otherFiltersAppearance === 'PAGE' && filtersOpen && filtersDetails[otherFiltersAppearance]}
      {!(otherFiltersAppearance === 'PAGE' && filtersOpen) && (
        <Stack>
          {filtersAndActions}
          <Grid container item xs>
            <GraphQLDataGrid<QueryData, QueryRecordType, QueryVariables> {...graphqlDatagridProps} variables={graphQLDataGridVariables} ref={ref} />
          </Grid>
        </Stack>
      )}
    </>
  )
}

const getFiltersSerializations = <FilterValue,>(filterValue: FilterValue | undefined, filters: Filter<FilterValue>[]): { filter: Filter<FilterValue>; serialization: string }[] => {
  if (filterValue) {
    return (filters || [])
      .map((f) => ({ filter: f, serialization: f.serialization(filterValue) }))
      .filter((f) => f.serialization !== undefined)
      .map((f) => ({ ...f, serialization: `${f.filter.label}: ${f.serialization}` }))
  } else {
    return []
  }
}

type FilterBuilderProps<FilterValueType extends object, Key extends keyof FilterValueType> = {
  key: Key
  label: string
  component: (props: { value: FilterValueType[Key] | undefined; onChange: (value: FilterValueType[Key]) => void }) => React.ReactNode & JSX.Element
  primary?: boolean
}

export const buildFilter = <FilterValueType extends object, Key extends keyof FilterValueType>(props: FilterBuilderProps<FilterValueType, Key>): Filter<FilterValueType> => {
  return {
    label: props.label,
    component: ({ value, onChange }) => {
      return props.component({
        value: value ? value[props.key] : undefined,
        onChange: (v) => onChange({ ...value, [props.key]: v } as FilterValueType),
      })
    },
    reset: (v) => {
      const res = { ...v }
      delete res[props.key]
      return res
    },
    serialization: (v) => `${v[props.key]}`,
    primary: props.primary ?? true,
  }
}

export default RichGraphQLDataGrid
